import { FilterCriteria } from '../../constants';
import { checkIsBooleanField, getValueTypesByField } from '../../utils';
import { FilterTokenizer } from './FilterTokenizer';

export interface TokenInfo {
  fields: string[];
  arithmeticOperators: string[];
  values: any[];
  logicalOperators: string[];
}

export function extractTokenInfo(
  value: string,
  filterOptions: Record<string, FilterCriteria>
): TokenInfo {
  const { tokens } = FilterTokenizer.tokenize(value);

  const fields: string[] = [];
  const arithmeticOperators: string[] = [];
  const values: any[] = [];
  const logicalOperators: string[] = [];

  tokens.forEach((token) => {
    switch (token.type) {
      case 'field':
        fields.push(token.value.toLowerCase());

        // Handle boolean fields - add placeholders like the original function
        if (
          checkIsBooleanField(getValueTypesByField(token.value, filterOptions))
        ) {
          arithmeticOperators.push('BOOL');
          values.push(true);
        }
        break;

      case 'operator':
        arithmeticOperators.push(token.value);
        break;

      case 'value':
        values.push(token.value);
        break;

      case 'logical':
        logicalOperators.push(token.value);
        break;
    }
  });

  return {
    fields,
    arithmeticOperators,
    values,
    logicalOperators,
  };
}

export function validateFilterSyntax(
  value: string,
  filterOptions: Record<string, FilterCriteria>
): { isValid: boolean; errorMessage?: string } {
  if (!value || value.trim().length === 0) {
    return { isValid: false, errorMessage: 'Definition is required' };
  }

  const { tokens } = FilterTokenizer.tokenize(value);
  const { fields, logicalOperators } = extractTokenInfo(value, filterOptions);

  // Check if we have at least one field
  if (fields.length === 0) {
    return { isValid: false, errorMessage: 'At least one field is required' };
  }

  // Check if logicalOperators has a length of fields minus one
  if (logicalOperators.length !== fields.length - 1) {
    return {
      isValid: false,
      errorMessage: 'Invalid logical operator count',
    };
  }

  // Check for balanced parentheses
  let openParens = 0;
  for (const token of tokens) {
    if (token.type === 'parenthesis') {
      if (token.value === '(') {
        openParens++;
      } else if (token.value === ')') {
        openParens--;
        if (openParens < 0) {
          return {
            isValid: false,
            errorMessage: 'Unmatched closing parenthesis',
          };
        }
      }
    }
  }

  if (openParens !== 0) {
    return {
      isValid: false,
      errorMessage: 'Unbalanced parentheses',
    };
  }

  // Validate token sequence (field -> operator -> value pattern)
  const nonParenTokens = tokens.filter((t) => t.type !== 'parenthesis');
  for (let i = 0; i < nonParenTokens.length; i++) {
    const token = nonParenTokens[i];
    const nextToken = nonParenTokens[i + 1];

    switch (token.type) {
      case 'field':
        // Field must be followed by operator (unless it's a boolean field)
        if (nextToken && nextToken.type !== 'operator') {
          const fieldName = token.value;
          if (
            !checkIsBooleanField(getValueTypesByField(fieldName, filterOptions))
          ) {
            return {
              isValid: false,
              errorMessage: `Field "${fieldName}" must be followed by an operator`,
            };
          }
        }
        break;

      case 'operator':
        // Operator must be followed by value
        if (!nextToken || nextToken.type !== 'value') {
          return {
            isValid: false,
            errorMessage: `Operator "${token.value}" must be followed by a value`,
          };
        }
        break;

      case 'value':
        // Value can be followed by logical operator or nothing
        if (nextToken && nextToken.type !== 'logical') {
          return {
            isValid: false,
            errorMessage: `Value must be followed by a logical operator or end of expression`,
          };
        }
        break;

      case 'logical':
        // Logical operator must be followed by field
        if (!nextToken || nextToken.type !== 'field') {
          return {
            isValid: false,
            errorMessage: `Logical operator "${token.value}" must be followed by a field`,
          };
        }
        break;
    }
  }

  return { isValid: true };
}

